import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { BadgeCheck, Facebook, Film, ImageIcon, Instagram, LayoutTemplate, Music2, Pin, Youtube, type LucideIcon } from 'lucide-react';
import type { MediaBrief, Network, PostStatus, SocialPost } from '@/types/social';
import { NETWORK_LABELS, POST_STATUS_LABELS } from '@/types/social';
import { colorClasses } from '@/utils/colors';
import { STATUS_CHIP, isApproved } from '@/utils/social';

const NETWORK_ICON: Record<Network, LucideIcon> = {
  instagram: Instagram,
  facebook: Facebook,
  pinterest: Pin,
  tiktok: Music2,
  youtube: Youtube,
};

const NETWORK_TEXT: Record<Network, string> = {
  instagram: 'text-pink-600',
  facebook: 'text-blue-600',
  pinterest: 'text-red-600',
  tiktok: 'text-slate-900',
  youtube: 'text-red-600',
};

export const NetworkIcon: React.FC<{ network: Network; size?: number; className?: string; colored?: boolean }> = ({
  network,
  size = 14,
  className,
  colored = true,
}) => {
  const Icon = NETWORK_ICON[network] ?? Pin;
  return (
    <span title={NETWORK_LABELS[network]} className={clsx('inline-flex', colored && NETWORK_TEXT[network], className)}>
      <Icon size={size} />
    </span>
  );
};

/** Redes do post (uma vez cada). */
export const PostNetworks: React.FC<{ post: SocialPost; size?: number }> = ({ post, size = 12 }) => {
  const nets = [...new Set(post.targets.map((t) => t.network))];
  if (nets.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1">
      {nets.map((n) => (
        <NetworkIcon key={n} network={n} size={size} />
      ))}
    </span>
  );
};

export const StatusChip: React.FC<{ status: PostStatus; className?: string }> = ({ status, className }) => (
  <span className={clsx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap', STATUS_CHIP[status], className)}>
    {isApproved(status) && status !== 'published' && <BadgeCheck size={11} />}
    {POST_STATUS_LABELS[status]}
  </span>
);

/** Selo verde de "aprovado pelo gestor" sobre a miniatura. */
export const ApprovedSeal: React.FC<{ status: PostStatus }> = ({ status }) =>
  isApproved(status) ? (
    <span className="rounded-full bg-white/95 p-0.5 shadow" title={`Aprovado — ${POST_STATUS_LABELS[status]}`}>
      <BadgeCheck size={14} className="text-emerald-600" />
    </span>
  ) : null;

export const PillarDot: React.FC<{ color?: string; name?: string; className?: string }> = ({ color, name, className }) =>
  name ? (
    <span className={clsx('inline-flex items-center gap-1 text-[11px] text-slate-600', className)}>
      <span className={clsx('inline-block w-2 h-2 rounded-full', colorClasses(color).swatch)} />
      {name}
    </span>
  ) : null;

/** Miniatura de uma mídia da biblioteca (SAS curta; sem retry — a tela recarrega a lista). */
export const BriefThumb: React.FC<{ media?: MediaBrief; className?: string; focalX?: number; focalY?: number }> = ({
  media,
  className,
  focalX,
  focalY,
}) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [media?.thumb_url]);
  const Placeholder = media?.kind === 'video' ? Film : ImageIcon;
  const pos = focalX !== undefined && focalY !== undefined ? `${Math.round(focalX * 100)}% ${Math.round(focalY * 100)}%` : undefined;
  return (
    <div className={clsx('relative overflow-hidden bg-slate-100', className)}>
      {media?.thumb_url && !failed ? (
        <img
          src={media.thumb_url}
          alt={media.title}
          loading="lazy"
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
          style={pos ? { objectPosition: pos } : undefined}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-300">
          <Placeholder size={24} />
        </div>
      )}
      {media?.kind === 'video' && (
        <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 text-[10px] text-white">vídeo</span>
      )}
    </div>
  );
};

/** Capa do post na grade/calendário: a 1ª mídia, ou o bloco colorido do pilar (estratégia). */
export const PostCover: React.FC<{ post: SocialPost; className?: string }> = ({ post, className }) => {
  const first = post.media.find((m) => m.target_id === undefined || m.target_id === null) ?? post.media[0];
  if (!first) {
    const cc = colorClasses(post.pillar_color);
    return (
      <div className={clsx('flex flex-col items-center justify-center text-center p-2 border-2 border-dashed', cc.soft, cc.border, className)}>
        <LayoutTemplate size={20} className={cc.text} />
        <span className={clsx('mt-1 text-[11px] font-semibold leading-tight', cc.textStrong)}>{post.pillar_name ?? 'Sem pilar'}</span>
        <span className="mt-0.5 text-[10px] text-slate-600 line-clamp-2">{post.title}</span>
      </div>
    );
  }
  // Reels com imagem de capa: a grade mostra a capa, como no perfil da rede.
  const covered = post.media.find((m) => m.cover && !m.cover.deleted);
  if (covered?.cover) return <BriefThumb media={covered.cover} className={className} />;
  return <BriefThumb media={first.media} focalX={first.focal_x} focalY={first.focal_y} className={className} />;
};
